/**
 * We haven't change the old code here only segracate this code from timer to here for better redability
 * ToDo: Changes mentioned in the CR for this file
 * ref: https://github.com/doselect/do-test-interface/pull/252
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import _isEmpty from 'lodash/isEmpty';

import {
  combineFailedSlugs,
  getFailedUploadQuestionNumbers,
  getCurrentTime,
  getCurrentTimedSection,
  isLastTimedSection,
  prepareSubmitMessage,
  prepareSubmitSectionMessage,
} from '../../../utils/utils';
import ConfirmModal, { ConfirmModel } from '../ConfirmModal/ConfirmModal';
import {
  getServerTime,
  setTestEndTime,
  submitSection,
  submitTest,
} from '../../../actions/test';
import {
  hideAutoSubmitModalAction,
  setAutoSubmitReason,
  setTimerExpired,
} from '../../../actions/appState';
import {
  leaveTestViolationAction,
  setProctorTestSubmit,
} from '../../../actions/proctor';
import { popUpClick, popUpView } from '../../../utils/trackingUtil';
import { AUTO_SUBMIT_REASONS, POPUP_NAME } from '../../../constants/Constants';
import { setWaitTillVideoSubmit } from '../../../actions/videoProblem';
import { SESSION_STATE } from '../../../constants/AssessmentConstants';

const AutoSubmitModal = () => {
  const dispatch = useDispatch();
  const {
    attemptedProblems,
    showAutoSubmitModal,
    test,
    testSolutionSet,
    autoSubmitReason,
    assessmentId,
    browserToleranceCount,
    indexedProblems,
    solutions,
  } = useSelector(
    state => ({
      attemptedProblems: state.appStatus.attemptedProblems,
      showAutoSubmitModal: state.appStatus.showAutoSubmitModal,
      test: state.test,
      testSolutionSet: state.testSolutionSet,
      autoSubmitReason: state.appStatus.autoSubmitReason,
      assessmentId: state.assessment.assessmentId,
      browserToleranceCount: state.proctor.browserToleranceCount,
      indexedProblems: state.problems.indexedProblems,
      solutions: state.solutions || {},
    }),
    shallowEqual
  );

  const uploadFailedSlugs = combineFailedSlugs(
    test.isAudioUploadInProgress,
    test.isVideoUploadFailed
  );

  const uploadFailedQuestions = getFailedUploadQuestionNumbers(
    uploadFailedSlugs,
    indexedProblems
  );

  const { waitTillVideoSubmit } = test;
  const [modalConfig, setModalConfig] = useState(new ConfirmModel({}));
  const timerRef = useRef(null);

  const { test_type_verbose } = test;

  const submit_test = useCallback(() => {
    if (!test.is_expired) {
      dispatch(submitTest(true));
    }
  }, [test]);

  const submit_section = useCallback(
    (solutionSetId, secSlug, isForce) => {
      if (!test.is_expired) {
        dispatch(submitSection(solutionSetId, secSlug, isForce));
      }
    },
    [test]
  );

  const isBrowserToleranceEnabled = useMemo(() => {
    const isProctorEnabled = test?.settings?.proctor?.enabled;
    const tempBrowserTolerance =
      test?.settings?.proctor?.browser_tolerance?.enabled;
    return (
      tempBrowserTolerance &&
      isProctorEnabled &&
      test.session_state === SESSION_STATE.ACTIVE
    );
  }, [test]);

  const toleranceCount = useMemo(() => {
    if (typeof browserToleranceCount === 'number') return browserToleranceCount;
    return undefined;
  }, [browserToleranceCount]);

  const handleTimeout = useCallback(() => {
    if (isBrowserToleranceEnabled && toleranceCount < 0) {
      dispatch(hideAutoSubmitModalAction());
      return;
    }
    const { sections: testSolutionSetSections, id } = testSolutionSet;
    if (_isEmpty(testSolutionSetSections)) return;
    if (!test.is_timed_section) {
      const { titleText, bodyText, positiveBtnText } = prepareSubmitMessage(
        test,
        testSolutionSet,
        attemptedProblems,
        solutions,
        test_type_verbose,
        true
      );
      setModalConfig(
        () =>
          new ConfirmModel({
            onClose: () => {
              submit_test();
              if (timerRef.current) {
                clearTimeout(timerRef.current);
              }
              setModalConfig(config => ({ ...config, isOpen: false }));
              dispatch(hideAutoSubmitModalAction());
              popUpClick({
                payload: {
                  popUpTitle: POPUP_NAME.autoSubmit,
                  popUpAction: 'Test Auto Submit prompt after time expiry',
                  cta: 'Procced to next page',
                },
              });
            },
            text: bodyText,
            isOpen: true,
            centered: true,
            escapeToClose: false,
            clickOutsideToClose: false,
            title: titleText,
            positiveBtnText,
          })
      );
      popUpView({
        payload: {
          popUpTitle: POPUP_NAME.autoSubmit,
          popUpAction: 'Test Auto Submit prompt after time expiry',
        },
      });
      if (!test.isTestSubmitted) {
        dispatch(setProctorTestSubmit(true));
        if (!autoSubmitReason)
          dispatch(setAutoSubmitReason(AUTO_SUBMIT_REASONS.TEST_TIME_EXPIRY));
      }
      if (!timerRef.current) {
        const currentTestEndTime = getCurrentTime().toISOString();
        dispatch(setTestEndTime(assessmentId, currentTestEndTime));
        dispatch(getServerTime({ isLastCall: true }));
        timerRef.current = setTimeout(() => {
          // Todo:
          // Test submits after 8 seconds of the actual test end. Need to fix it
          // Refer Jira: https://doselect.atlassian.net/browse/PF-1614
          dispatch(setWaitTillVideoSubmit(false));
          submit_test();
          setModalConfig(config => ({
            ...config,
            isOpen: false,
          }));
          dispatch(hideAutoSubmitModalAction());
        }, 8000);
      }
    } else if (test.is_timed_section) {
      const {
        bodyText,
        titleText,
        positiveBtnText,
      } = prepareSubmitSectionMessage(
        test,
        testSolutionSet,
        attemptedProblems,
        solutions,
        test_type_verbose,
        true,
        uploadFailedQuestions
      );
      const currentSection = getCurrentTimedSection(testSolutionSetSections);
      setModalConfig(
        () =>
          new ConfirmModel({
            onClose: () => {
              if (!_isEmpty(currentSection)) {
                if (!isLastTimedSection(testSolutionSetSections)) {
                  submit_section(id, currentSection.slug, true);
                } else {
                  submit_test();
                }
              }
              if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
              }
              setModalConfig(config => ({ ...config, isOpen: false }));
              dispatch(hideAutoSubmitModalAction());
              popUpClick({
                payload: {
                  popUpTitle: POPUP_NAME.autoSubmit,
                  popUpAction: `Section Auto Submit prompt to ${
                    !_isEmpty(currentSection) &&
                    !isLastTimedSection(testSolutionSetSections)
                      ? 'next section'
                      : 'feedback page'
                  } due to time expiry`,
                  cta: 'Procced to next section/page',
                },
              });
            },
            text: bodyText,
            isOpen: true,
            title: titleText,
            centered: true,
            escapeToClose: false,
            clickOutsideToClose: false,
            positiveBtnText,
          })
      );
      popUpView({
        payload: {
          popUpTitle: POPUP_NAME.autoSubmit,
          popUpAction: `Section Auto Submit prompt to ${
            !_isEmpty(currentSection) &&
            !isLastTimedSection(testSolutionSetSections)
              ? 'next section'
              : 'feedback page'
          } due to time expiry`,
        },
      });
      if (
        !_isEmpty(currentSection) &&
        isLastTimedSection(testSolutionSetSections) &&
        !test.isTestSubmitted
      ) {
        const currentTestEndTime = getCurrentTime().toISOString();
        dispatch(setTestEndTime(assessmentId, currentTestEndTime));
        dispatch(setProctorTestSubmit(true));
      }
      if (!timerRef.current) {
        dispatch(getServerTime({ isLastCall: true }));
        timerRef.current = setTimeout(() => {
          dispatch(setWaitTillVideoSubmit(false));
          if (!_isEmpty(currentSection)) {
            if (!isLastTimedSection(testSolutionSetSections)) {
              setTimerExpired(false);
              submit_section(id, currentSection.slug, true);
            } else if (!test.isTestSubmitted) {
              if (!autoSubmitReason)
                dispatch(
                  setAutoSubmitReason(AUTO_SUBMIT_REASONS.TEST_TIME_EXPIRY)
                );
              submit_test();
            }
          }
          setModalConfig(config => ({
            ...config,
            isOpen: false,
          }));
          dispatch(hideAutoSubmitModalAction());
          clearInterval(timerRef.current);
          timerRef.current = null;
        }, 8000);
      }
    }
  }, [
    attemptedProblems,
    submit_section,
    submit_test,
    test,
    testSolutionSet,
    waitTillVideoSubmit,
    assessmentId,
    isBrowserToleranceEnabled,
    toleranceCount,
    autoSubmitReason,
  ]);

  useEffect(() => {
    if (showAutoSubmitModal) {
      handleTimeout();
    }
  }, [showAutoSubmitModal, handleTimeout]);

  useEffect(() => {
    if (toleranceCount < 0 && !showAutoSubmitModal) {
      dispatch(leaveTestViolationAction(true));
    }
  }, [toleranceCount, showAutoSubmitModal]);

  return <ConfirmModal config={modalConfig} />;
};

export default AutoSubmitModal;
